import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAppAuth } from '@/lib/useAppAuth';
import { FullPageSpinner, Skeleton } from '@/components/ui';
import {
  BinaryMarketPageSkeleton,
  BottomNav,
  Footer,
  HomeTopBar,
  SearchOverlayBody,
  SideNav,
  SnakeMarketPageSkeleton,
} from '@/components/features';
import { useIsDesktop } from '@/lib/notifications/useIsDesktop';
import { useSearchOverlayOpen } from '@/store';
import { useAnalyticsClient, usePageViewTracking } from '@/analytics';
import { ProtectedRoute } from './ProtectedRoute';
import { ScrollToTop } from './ScrollToTop';
import { HomePage } from '@/pages/HomePage';
import { WalletPage } from '@/pages/WalletPage';
import { PortfolioPage } from '@/pages/PortfolioPage';
import { LivePage } from '@/pages/LivePage';
import { LoyaltyPage } from '@/pages/LoyaltyPage';

const SettingsPage = lazy(() => import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));

const EventDetailPage = lazy(() => import('@/pages/EventDetailPage').then((m) => ({ default: m.EventDetailPage })));

const SnakeMarketPage = lazy(() => import('@/pages/SnakeMarketPage').then((m) => ({ default: m.SnakeMarketPage })));

const RealtimeDebugPage = lazy(() => import('@/pages/RealtimeDebugPage').then((m) => ({ default: m.RealtimeDebugPage })));

/**
 * Chunk-load fallbacks for the lazy event/snake routes: the page's own
 * skeleton inside the page container (back-link slot reserved), so
 * navigation goes straight into the skeleton instead of flashing a
 * full-page spinner first and the in-page loading state second.
 */
function EventDetailPageFallback() {
  return (
    <div className="flex flex-1 flex-col px-4 pb-10 md:px-6 lg:px-8 xl:px-10">
      <Skeleton className="mt-2 h-5 w-16 rounded" />
      <div className="mt-6">
        <BinaryMarketPageSkeleton />
      </div>
    </div>
  );
}

function SnakeMarketPageFallback() {
  return (
    <div className="flex min-h-svh flex-col px-4 pb-44 md:px-6 md:pb-10 lg:px-8 xl:px-10">
      <Skeleton className="mt-2 h-5 w-24 rounded" />
      <div className="mx-auto mt-6 w-full max-w-[640px] lg:max-w-[1460px]">
        <SnakeMarketPageSkeleton />
      </div>
    </div>
  );
}

/**
 * Single page-view tracker mounted just inside <BrowserRouter>.
 */
function PageViewTracker() {
  const analytics = useAnalyticsClient();
  usePageViewTracking(analytics);
  return null;
}

/**
 * Inner shell rendered inside `<BrowserRouter>`. Owns the singleton
 * `<HomeTopBar />` and `<BottomNav />` so they survive route transitions
 * and aren't duplicated by individual pages. The nav is hidden on
 * full-bleed routes that intentionally don't reserve space for it
 * (currently `/events/*`).
 */
function AppShell() {
  const { isAuthenticated } = useAppAuth();
  // The full-page search takeover is a mobile pattern: on desktop (md+) search
  // renders as a popover anchored to the header field, so the routed page stays
  // visible and the overlay body must not mount (its click-away/close listeners
  // and fetches belong to the takeover). JS-gated rather than CSS-hidden per the
  // useMediaQuery rule: the two layouts must not co-mount.
  const searchOpen = useSearchOverlayOpen();
  const isDesktop = useIsDesktop();
  const searchTakeover = searchOpen && !isDesktop;

  // Drive the root layout width from auth state (see `#root` rules in index.css):
  // authenticated → full-width column offset by the sidebar; guest → centered, capped.
  useEffect(() => {
    document.documentElement.classList.toggle('is-authenticated', isAuthenticated);
  }, [isAuthenticated]);

  return (
    <>
      <HomeTopBar />
      <Suspense fallback={<FullPageSpinner className="min-h-0 flex-1" />}>
        {/* Grows to fill the viewport under the top bar (see #root flex rules);
            swapped to display:none while the mobile search overlay owns the screen. */}
        <div className={searchTakeover ? 'hidden' : 'flex min-w-0 flex-1 flex-col'}>
          <Routes>
            <Route element={<ProtectedRoute />}>
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/live" element={<LivePage />} />
              <Route path="/loyalty" element={<LoyaltyPage />} />
            </Route>
            <Route
              path="/events/:eventId"
              element={
                <Suspense fallback={<EventDetailPageFallback />}>
                  <EventDetailPage />
                </Suspense>
              }
            />
            {['/snake', '/snake/:instrument', '/snake/:instrument/:subCategoryCode'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <Suspense fallback={<SnakeMarketPageFallback />}>
                    <SnakeMarketPage />
                  </Suspense>
                }
              />
            ))}
            <Route path="/dev/realtime" element={<RealtimeDebugPage />} />
            <Route path="/*" element={<HomePage />} />
          </Routes>
          {/* Pinned to the bottom of scrollable content on standard pages; self-gates via useFooterVisible. */}
          <Footer />
        </div>
        {!isDesktop && <SearchOverlayBody />}
      </Suspense>
      {/* Adaptive nav: bottom bar below md, sidebar at md+ (each self-hides via CSS). */}
      {isAuthenticated && (
        <>
          <BottomNav />
          <SideNav />
        </>
      )}
    </>
  );
}

/**
 * Application route tree. Renders inside the `<BrowserRouter>` mounted by
 * {@link App} (which also scopes the global overlays), so it doesn't create
 * its own router.
 *
 * Waits for auth initialization before rendering routes.
 * The home page is accessible to all users — auth modals handle
 * login/signup instead of dedicated pages.
 */
export function AppRouter() {
  const { isLoading } = useAppAuth();

  if (isLoading) {
    return <FullPageSpinner />;
  }

  return (
    <>
      <PageViewTracker />
      <ScrollToTop />
      <AppShell />
    </>
  );
}
